import type { BusinessReportOutcome, BusinessReportOutcomeResult } from "@/modules/snapshots/businessReports.types";

import type {
  ContractFamilyRole,
  PipelineSummaryGridCell,
  PipelineSummaryGridIssue,
  PipelineSummaryGridField,
  PipelineSummaryGridResponse,
  PipelineSummaryGridRow,
} from "./pipelinePreview.types";

export interface SnapshotGridCellIssue {
  familyId: string;
  familyName: string;
  fieldId: string;
  fieldName: string;
  code: "summary_generation_failed";
}

export interface SnapshotGridCellModel {
  id: string;
  fieldId: string | null;
  fieldKey: string;
  name: string;
  value: string;
  date: string | null;
  rating: string | null;
  overridden: boolean;
  issue: SnapshotGridCellIssue | null;
}

export interface SnapshotGridRowModel {
  id: string;
  name: string;
  cells: SnapshotGridCellModel[];
  sourceOutcome?: BusinessReportOutcome;
  presentation: {
    kind: "snapshot" | "family_summary" | "source_document";
    familyId: string | null;
    role: ContractFamilyRole | null;
    stagedOutcomeId: string | null;
    /** Materialized `business_report_outcome` backing this row, when one exists. */
    outcomeId: string | null;
    editable: boolean;
    supplierName?: string | null;
    contractFamilyOwnerUserId?: string | null;
    summaryRowKind?: "contract_summary" | "document_summary" | null;
    /** Family rows whose folder was deleted stay in the collection, frozen and marked. */
    folderDeletedAt?: string | null;
    /** Family rows whose folder lost all documents stay in the collection, frozen and marked. */
    folderEmptiedAt?: string | null;
  };
}

export interface AdaptedPipelineSummaryGridPage {
  fields: PipelineSummaryGridField[];
  rows: SnapshotGridRowModel[];
  sourceCollectionId: string;
  templateId: string;
  pagination: {
    unit: "family";
    offset: number;
    limit: number;
    total: number;
  };
  issueTotal: number;
  issues: SnapshotGridCellIssue[];
}

const adaptSnapshotCell = (cell: BusinessReportOutcomeResult): SnapshotGridCellModel => ({
  id: cell.id,
  fieldId: cell.business_report_prompt_id,
  fieldKey: cell.business_report_prompt_id ?? cell.id,
  name: cell.name,
  value: cell.result,
  date: cell.date,
  rating: cell.rating,
  overridden: false,
  issue: null,
});

const adaptPipelineIssue = (issue: PipelineSummaryGridIssue): SnapshotGridCellIssue => ({
  familyId: issue.revision_family_id,
  familyName: issue.family_name,
  fieldId: issue.field_id,
  fieldName: issue.field_name,
  code: issue.code,
});

const findPipelineIssue = (
  issues: SnapshotGridCellIssue[],
  row: PipelineSummaryGridRow,
  cell: PipelineSummaryGridCell,
): SnapshotGridCellIssue | null => {
  if (row.row_kind !== "family_summary") return null;
  return issues.find(issue => issue.familyId === row.family_id && issue.fieldId === cell.field_id) ?? null;
};

const contractFamilyRoles = new Set<ContractFamilyRole>(["MASTER", "AMENDMENT", "CHILD", "ANCILLARY", "NON_RELEVANT"]);

const adaptContractFamilyRole = (role: string | null): ContractFamilyRole | null =>
  role !== null && contractFamilyRoles.has(role as ContractFamilyRole) ? (role as ContractFamilyRole) : null;

const adaptPipelineRow = (issues: SnapshotGridCellIssue[], row: PipelineSummaryGridRow): SnapshotGridRowModel => ({
  id: row.id,
  name: row.name,
  cells: row.cells.map(cell => ({
    id: cell.field_id,
    fieldId: cell.field_id,
    fieldKey: cell.field_key,
    name: cell.name,
    value: cell.value,
    date: cell.date,
    rating: cell.rating,
    overridden: cell.overridden,
    issue: findPipelineIssue(issues, row, cell),
  })),
  presentation: {
    kind: row.row_kind,
    familyId: row.family_id,
    role: adaptContractFamilyRole(row.role),
    stagedOutcomeId: row.staged_outcome_id,
    outcomeId: row.row_kind === "family_summary" ? row.projected_outcome_id : row.source_outcome_id,
    editable: row.editable,
    supplierName: row.supplier_name,
    contractFamilyOwnerUserId: row.contract_family_owner_user_id,
    summaryRowKind: row.row_kind === "family_summary" ? "contract_summary" : null,
    folderDeletedAt: null,
    folderEmptiedAt: null,
  },
});

export const adaptSnapshotOutcomeRow = (outcome: BusinessReportOutcome): SnapshotGridRowModel => ({
  id: outcome.id,
  name: outcome.name,
  cells: outcome.result.map(adaptSnapshotCell),
  sourceOutcome: outcome,
  presentation: {
    kind: outcome.summary_row_kind ? "family_summary" : "snapshot",
    familyId: outcome.summary_row_kind === "contract_summary" ? (outcome.logical_family_id ?? null) : null,
    role: null,
    stagedOutcomeId: null,
    outcomeId: outcome.id,
    editable: false,
    supplierName: outcome.supplier_name,
    contractFamilyOwnerUserId: outcome.contract_family_owner_user_id,
    summaryRowKind: outcome.summary_row_kind,
    folderDeletedAt: outcome.folder_deleted_at ?? null,
    folderEmptiedAt: outcome.folder_emptied_at ?? null,
  },
});

export const adaptPipelineSummaryGridPage = (page: PipelineSummaryGridResponse): AdaptedPipelineSummaryGridPage => {
  const issues = page.issues.map(adaptPipelineIssue);

  return {
    fields: page.fields,
    rows: page.groups.flatMap(group => group.rows.map(row => adaptPipelineRow(issues, row))),
    sourceCollectionId: page.source_collection_id,
    templateId: page.template_id,
    pagination: {
      unit: "family",
      offset: page.offset,
      limit: page.limit,
      total: page.total_groups,
    },
    issueTotal: page.issue_total,
    issues,
  };
};
